// quota-sleep-scheduler.ts
//
// Hết ngạch (quota/rate-limit) ⇒ tính `wake_at`, ghi BỀN, rồi TRẢ VỀ NGAY.
// Không `setTimeout`, không await một timer: vòng lặp điều khiển của
// supervisor phải còn nhận được pause/abort trong lúc "đang ngủ" (test 47).
// Ai muốn biết đã tới giờ chưa thì gọi `isAwake(state, now)`.
//
// ⛔ CẤM literal ngưỡng §18 ở đây — thang backoff PHẢI đến từ
// `QuotaConfig.backoff_min` được tiêm. Đặc biệt KHÔNG import
// `BACKOFF_LADDER_MINUTES` của rate-limit-detector.ts: thang đó là hằng nội bộ
// của module parse text, dùng lại nó sẽ khiến `backoff_min` trong config của
// user trở thành trang trí. Gate tĩnh:
// test/lib/autopilot/section18-threshold-ast-gate.test.ts.
//
// Vì sao ghi FILE RIÊNG thay vì thêm event kind vào journal: state này là một
// ô nhớ ghi-đè (chỉ bản mới nhất có nghĩa), không phải chuỗi sự kiện cần
// replay. Journal giữ nguyên bộ delta của P5 — YAGNI.
import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { type DurableFsOps, writeFileDurable } from "./durable-file-writer.js";
import { parseResetTime, resetInstantInZone, resetInstantUtc } from "./quota-reset-time-parser.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

export type QuotaConfig = Pick<AutopilotConfig, "backoff_min">;

export interface QuotaSleepState {
  wake_at: string;
  reason: string;
  backoff_index: number;
  attempt_log_name: string;
}

const MS_PER_MINUTE = 60_000;

/**
 * Bậc backoff kế tiếp, đọc thẳng từ thang được tiêm. Vượt cuối thang thì GIỮ
 * bậc cuối (trần), không quay vòng và không nhân tiếp — người vận hành khai
 * bậc cuối là đã tuyên bố "chờ lâu nhất chừng này".
 */
export function nextBackoffMinutes(config: QuotaConfig, backoffIndex: number): number {
  const ladder = config.backoff_min;
  if (ladder.length === 0) {
    throw new Error("quota-sleep: backoff_min rỗng — không suy ra được thời gian chờ.");
  }
  const clamped = Math.min(Math.max(backoffIndex, 0), ladder.length - 1);
  const minutes = ladder[clamped];
  if (minutes === undefined) {
    throw new Error(`quota-sleep: backoff_min[${clamped}] không đọc được.`);
  }
  return minutes;
}

export interface WakeAtComputation {
  wakeAt: Date;
  /** true = suy từ thang backoff; false = lấy từ reset-time nhà cung cấp báo. */
  usedLadder: boolean;
}

/**
 * Ưu tiên giờ reset do nhà cung cấp nói ra (chính xác hơn mọi phỏng đoán);
 * không parse được mới rơi về thang backoff đã tiêm.
 */
export function computeWakeAt(
  now: Date,
  resetTimeText: string | undefined,
  config: QuotaConfig,
  backoffIndex: number,
): WakeAtComputation {
  // 06g R8 (debate B7): "(Asia/Saigon)" after the hour ⇒ wall-clock in that
  // zone; no zone ⇒ UTC as before (quota-reset-time-parser.ts has both).
  const reset = parseResetTime(resetTimeText);
  if (reset) {
    const wakeAt =
      reset.zone === undefined
        ? resetInstantUtc(now, reset)
        : resetInstantInZone(now, reset, reset.zone);
    if (wakeAt) return { wakeAt, usedLadder: false };
  }

  const minutes = nextBackoffMinutes(config, backoffIndex);
  return { wakeAt: new Date(now.getTime() + minutes * MS_PER_MINUTE), usedLadder: true };
}

/** Ghi BỀN (tmp → fsync → rename → fsync dir) — mất điện giữa chừng vẫn đọc lại được. */
export async function persistQuotaSleepState(
  path: string,
  state: QuotaSleepState,
  ops?: DurableFsOps,
): Promise<void> {
  const bytes = Buffer.from(`${JSON.stringify(state, null, 2)}\n`, "utf8");
  await writeFileDurable(path, bytes, ops === undefined ? undefined : { ops });
}

/**
 * Thiếu file / JSON hỏng ⇒ `undefined` (chưa từng ngủ, cứ chạy tiếp), KHÔNG
 * throw — giống `readProjection`: state phái sinh không được làm sập khởi động.
 */
/** 06h R5a (dogfood 7 F-41): remove the sleep file once the loop passes `wake_at` — `status` stops printing a stale `quota wake_at`. Missing file ⇒ no-op. */
export async function clearQuotaSleepState(path: string): Promise<void> {
  await fs.rm(path, { force: true });
}

export async function readQuotaSleepState(path: string): Promise<QuotaSleepState | undefined> {
  try {
    const parsed = JSON.parse(await fs.readFile(path, "utf8")) as Partial<QuotaSleepState>;
    if (typeof parsed.wake_at !== "string") return undefined;
    if (typeof parsed.reason !== "string") return undefined;
    if (typeof parsed.backoff_index !== "number") return undefined;
    if (typeof parsed.attempt_log_name !== "string") return undefined;
    return parsed as QuotaSleepState;
  } catch {
    return undefined;
  }
}

export interface EnterQuotaSleepInput {
  statePath: string;
  now: Date;
  reason: string;
  config: QuotaConfig;
  backoffIndex: number;
  resetTimeText?: string;
  /** Checkpoint (khôi phục checkout, dọn worktree…) — chạy TRƯỚC khi lưu state. */
  beforeSleep?: () => Promise<void> | void;
  onPersisted?: () => void;
  ops?: DurableFsOps;
}

/**
 * Vào trạng thái ngủ vì quota. Thứ tự BẮT BUỘC: `beforeSleep` → ghi state.
 * Ngược lại, crash giữa hai bước sẽ để lại "đang ngủ" trên một checkout chưa
 * được khôi phục, và lần khởi động sau tin rằng mọi thứ đã sạch.
 */
export async function enterQuotaSleep(input: EnterQuotaSleepInput): Promise<QuotaSleepState> {
  const { wakeAt } = computeWakeAt(
    input.now,
    input.resetTimeText,
    input.config,
    input.backoffIndex,
  );

  if (input.beforeSleep) await input.beforeSleep();

  const state: QuotaSleepState = {
    wake_at: wakeAt.toISOString(),
    reason: input.reason,
    backoff_index: input.backoffIndex,
    // UUID: hai lần quota-retry liên tiếp KHÔNG được trùng tên log, nếu không
    // lần sau ghi đè bằng chứng của lần trước (test 48).
    attempt_log_name: `attempt-${randomUUID()}`,
  };

  await persistQuotaSleepState(input.statePath, state, input.ops);
  input.onPersisted?.();
  return state;
}

/** Đã tới giờ dậy chưa. Thuần — supervisor tự poll, không ai bị chặn. */
export function isAwake(state: QuotaSleepState, now: Date): boolean {
  return now.getTime() >= new Date(state.wake_at).getTime();
}

/**
 * Số lần chạy VẬT LÝ luôn tăng, kể cả khi lần chạy đó chết vì quota. Tách
 * thành hàm riêng để chỗ nào cũng đi qua một đường — bỏ tăng ở nhánh quota là
 * cách dễ nhất làm trùng tên log giữa hai lần thử.
 */
export function incrementPhysicalRunCount(current: number): number {
  return current + 1;
}
